import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Download, Banknote, Clock, CheckCircle2, AlertTriangle, Archive, ArchiveRestore, PlusCircle, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/dateFormat";

const STATUS_META = {
  submitted:     { label: "Submitted",      tone: "bg-[#0A192F]/5 text-[#4A5568] border-[#0A192F]/15", icon: Clock },
  in_collection: { label: "In collection",  tone: "bg-[#F59E0B]/10 text-[#92400E] border-[#F59E0B]/30", icon: Banknote },
  recovered:     { label: "Recovered",      tone: "bg-[#22C55E]/10 text-[#15803D] border-[#22C55E]/30", icon: CheckCircle2 },
  uncollectible: { label: "Uncollectible",  tone: "bg-[#EF4444]/10 text-[#B91C1C] border-[#EF4444]/30", icon: AlertTriangle },
  closed:        { label: "Closed",         tone: "bg-[#4A5568]/10 text-[#4A5568] border-[#4A5568]/30", icon: Archive },
};

export default function ClaimDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const isStaff = user?.role === "ntn_staff";

  const load = () => {
    apiClient.get(`/claims/${id}`).then(({ data }) => setC(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  };
  useEffect(load, [id]);

  const downloadPdf = async () => {
    try {
      const res = await apiClient.get(`/claims/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `NTN-Claim-${id.slice(0, 8).toUpperCase()}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const toggleArchive = async () => {
    try {
      const path = c.archived_at ? "unarchive" : "archive";
      const { data } = await apiClient.post(`/claims/${id}/${path}`);
      setC(data);
      toast.success(c.archived_at ? "Restored from archive" : "Archived");
    } catch (e) { toast.error("Could not update"); }
  };

  if (error) return <AppShell><div className="text-[#EF4444]">{error}</div></AppShell>;
  if (!c) return <AppShell><div className="text-[#4A5568]">Loading…</div></AppShell>;

  const meta = STATUS_META[c.status] || STATUS_META.submitted;
  const pct = c.total_amt_owed ? Math.min(100, (Number(c.total_recovered || 0) / c.total_amt_owed) * 100) : 0;

  return (
    <AppShell>
      <div className="mb-6">
        <Link to="/collections" className="inline-flex items-center text-sm text-[#4A5568] hover:text-[#0A192F]" data-testid="back-to-collections">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to collections
        </Link>
      </div>

      {/* Header */}
      <div className="bg-white border border-[#0A192F]/10 rounded-sm p-8 mb-6">
        {c.archived_at && (
          <div className="mb-5 inline-flex items-center gap-2 text-xs font-mono tracking-[0.14em] uppercase bg-[#F3EFEA] border border-[#0A192F]/15 px-3 py-1.5 rounded-sm" data-testid="claim-archived-banner">
            <Archive className="h-3.5 w-3.5" /> Archived {fmtDate(c.archived_at)}
          </div>
        )}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="min-w-0">
            <div className="font-overline text-[#4A5568] mb-2">Claim · {c.id.slice(0, 8).toUpperCase()}</div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#0A192F]" data-testid="claim-debtor-name">{c.debtor?.name}</h1>
            <div className="text-sm text-[#4A5568] mt-1.5">
              {c.subscriber?.company_name} · Submitted {fmtDate(c.created_at)}
            </div>
          </div>
          <div className="flex items-start gap-3 flex-wrap">
            <Badge variant="outline" className={`rounded-sm uppercase font-mono ${meta.tone}`} data-testid={`claim-status-${c.status}`}>
              <meta.icon className="h-3.5 w-3.5 mr-1.5" /> {meta.label}
            </Badge>
            <Button onClick={downloadPdf} size="sm" variant="outline" className="rounded-sm" data-testid="claim-pdf-btn">
              <Download className="h-4 w-4 mr-2" /> Request form PDF
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-sm" data-testid="claim-archive-btn">
                  {c.archived_at ? <><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</> : <><Archive className="h-4 w-4 mr-2" /> Archive</>}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{c.archived_at ? "Restore claim?" : "Archive claim?"}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {c.archived_at ? "It will reappear in your active list." : "It will be hidden from active claims but kept for your records."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={toggleArchive} className="bg-[#0A192F]" data-testid="claim-archive-confirm">
                    {c.archived_at ? "Restore" : "Archive"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Amount + recovery progress */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Metric label="Amount owed" value={`$${Number(c.total_amt_owed).toFixed(2)}`} />
          <Metric label="Recovered" value={`$${Number(c.total_recovered || 0).toFixed(2)}`} tone="text-[#15803D]" />
          <Metric label="Flat fee" value={`$${Number(c.fee_due_usd).toFixed(2)}`} sub="per claim" />
        </div>
        <div className="mt-4">
          <div className="h-2 rounded-full bg-[#0A192F]/10 overflow-hidden">
            <div className="h-full bg-[#22C55E]" style={{ width: `${pct}%`, transition: "width 1s ease-out" }} />
          </div>
          <div className="text-[10px] font-mono tracking-[0.12em] text-[#4A5568] mt-1">{pct.toFixed(0)}% RECOVERED</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left column */}
        <div className="lg:col-span-7 space-y-6">
          {isStaff && (
            <StaffActions c={c} onChanged={(updated) => setC(updated)} />
          )}

          <Section title="Debtor information" testid="section-debtor">
            <KV rows={[
              ["Name", c.debtor?.name],
              ["SSN (last 4)", c.debtor?.ssn_last4 ? `XXX-XX-${c.debtor.ssn_last4}` : "—"],
              ["Date of birth", c.debtor?.dob || "—"],
              ["Last known address", c.debtor?.last_known_address],
              ["City / State / ZIP", `${c.debtor?.city || ""}, ${c.debtor?.state || ""} ${c.debtor?.zip || ""}`],
              ["Phone 1", c.debtor?.phone1],
              ["Phone 2", c.debtor?.phone2 || "—"],
              ["Claim date", c.claim_date],
              ["Reason", c.reason_for_collection],
            ]} />
          </Section>

          <Section title="Subscriber information" testid="section-subscriber">
            <KV rows={[
              ["Company", c.subscriber?.company_name],
              ["Contact", c.subscriber?.contact_name],
              ["NTN Sub#", c.subscriber?.ntn_sub || "—"],
              ["Address", c.subscriber?.address],
              ["City / State / ZIP", `${c.subscriber?.city}, ${c.subscriber?.state} ${c.subscriber?.zip}`],
              ["Phone", c.subscriber?.phone],
              ["Email", c.subscriber?.email || "—"],
            ]} />
          </Section>

          <Section title="Certification" testid="section-cert">
            <KV rows={[
              ["Credit-bureau reporting", c.credit_bureau_authorization ? "INITIALED YES" : "INITIALED NO"],
              ["Signed by", c.certification?.signature_text],
              ["Signed at", fmtDateTime(c.certification?.signed_at || c.created_at)],
              ["IP address", c.certification?.ip || "—"],
            ]} />
          </Section>
        </div>

        {/* Right column */}
        <aside className="lg:col-span-5 space-y-6">
          <Section title="Recoveries" testid="section-recoveries">
            {!c.recoveries?.length ? (
              <div className="text-sm text-[#4A5568]">No recoveries recorded yet.</div>
            ) : (
              <ul className="divide-y divide-[#0A192F]/8">
                {c.recoveries.map((r) => (
                  <li key={r.id} className="py-3 flex items-start justify-between gap-3 text-sm" data-testid={`recovery-${r.id}`}>
                    <div className="min-w-0">
                      <div className="font-serif text-base text-[#15803D]">${Number(r.amount).toFixed(2)}</div>
                      <div className="text-xs text-[#4A5568]">{fmtDate(r.date)} · {r.recorded_by_name}</div>
                      {r.note && <div className="text-xs text-[#4A5568] mt-1 italic">{r.note}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Status timeline" testid="section-history">
            <ol className="relative border-l border-[#0A192F]/15 pl-5 space-y-4">
              {(c.status_history || []).map((h, i) => {
                const sm = STATUS_META[h.status] || STATUS_META.submitted;
                return (
                  <li key={i} className="relative">
                    <span className="absolute -left-[27px] top-1 h-2 w-2 rounded-full bg-[#0A192F]" />
                    <div className="font-semibold text-sm text-[#0A192F] inline-flex items-center gap-1.5">
                      <sm.icon className="h-3.5 w-3.5" /> {sm.label}
                    </div>
                    <div className="text-xs text-[#4A5568]">{fmtDateTime(h.at)} · {h.by_name}</div>
                    {h.note && <div className="text-xs text-[#4A5568] mt-0.5 italic">{h.note}</div>}
                  </li>
                );
              })}
            </ol>
          </Section>

          <div className="bg-[#0A192F] text-white rounded-sm p-6">
            <div className="flex items-center gap-2 mb-2"><FileSignature className="h-4 w-4" /><div className="font-overline text-white/70">Service Agreement</div></div>
            <div className="font-serif text-lg">On file</div>
            <div className="text-xs text-white/60 mt-1 font-mono">ID {c.service_agreement_id?.slice(0, 8).toUpperCase()}</div>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}

/* ---------------- Staff Actions ---------------- */
function StaffActions({ c, onChanged }) {
  const [status, setStatus] = useState(c.status);
  const [note, setNote] = useState("");
  const [recAmount, setRecAmount] = useState("");
  const [recNote, setRecNote] = useState("");
  const [busy, setBusy] = useState(false);

  const saveStatus = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.patch(`/claims/${c.id}/status`, { status, note: note || null });
      onChanged(data); setNote(""); toast.success("Status updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  const addRecovery = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/claims/${c.id}/recoveries`,
        { amount: parseFloat(recAmount), note: recNote || null });
      onChanged(data); setRecAmount(""); setRecNote(""); toast.success("Recovery logged");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6 sm:p-8" data-testid="staff-actions">
      <div className="font-overline text-[#4A5568]">NTN Staff actions</div>
      <h2 className="font-serif text-2xl text-[#0A192F] mt-1 mb-5">Manage this claim</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Update status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 rounded-sm" data-testid="staff-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).map(([v, m]) => (
                <SelectItem key={v} value={v}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)" className="rounded-sm" data-testid="staff-status-note" />
          <Button onClick={saveStatus} disabled={busy} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-10 w-full" data-testid="staff-status-save">
            Update status
          </Button>
        </div>

        <div className="space-y-3">
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Log a recovery</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]">$</span>
            <Input type="number" step="0.01" value={recAmount} onChange={(e) => setRecAmount(e.target.value)}
              placeholder="500.00" className="h-11 rounded-sm pl-7" data-testid="staff-rec-amount" />
          </div>
          <Textarea rows={2} value={recNote} onChange={(e) => setRecNote(e.target.value)}
            placeholder="Note (e.g., money order, partial)" className="rounded-sm" data-testid="staff-rec-note" />
          <Button onClick={addRecovery} disabled={busy || !recAmount} variant="outline" className="rounded-sm h-10 w-full border-[#22C55E]/30 text-[#15803D] hover:bg-[#22C55E]/10" data-testid="staff-rec-add">
            <PlusCircle className="h-4 w-4 mr-2" /> Log recovery
          </Button>
          <div className="text-[10px] text-[#4A5568] font-mono tracking-[0.1em]">
            AUTO-MARKS RECOVERED WHEN TOTAL ≥ ${Number(c.total_amt_owed).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Helpers ---------------- */
function Section({ title, children, testid }) {
  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6" data-testid={testid}>
      <h3 className="font-serif text-xl text-[#0A192F] mb-4">{title}</h3>
      {children}
    </div>
  );
}

function KV({ rows }) {
  return (
    <dl className="divide-y divide-[#0A192F]/8">
      {rows.map(([k, v]) => (
        <div key={k} className="py-2.5 grid grid-cols-2 gap-3 text-sm">
          <dt className="text-[#4A5568]">{k}</dt>
          <dd className="text-[#0A192F] break-words">{v || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function Metric({ label, value, sub, tone = "text-[#0A192F]" }) {
  return (
    <div>
      <div className="font-overline text-[#4A5568]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] font-mono text-[#4A5568]">{sub}</div>}
    </div>
  );
}
